'use client';

import React from 'react';
import { Brand } from '@/domainLogic/index.ts';
import type { HeaderProps as ContentHubHeaderProps } from '@lmn/contenthub-shared/components';
import { InnerSlot } from '@lmn/common-search-widget';
import ExternalHeader from '@/app/common/ExternalHeader.tsx';
import HtmlInjector from '@/utils/HTMLInjector.tsx';
import ContentHubHeader from '@/app/common/headerFooter/ContentHubHeader.tsx';
import StaticHeader from '@/app/common/headerFooter/StaticHeader.tsx';
import { isHeaderPropsValid } from '@/app/common/headerFooter/headerFooterUtils.ts';
import {
  CSWAppContainer,
  CSWAppExpandedOffsetReset,
  CSWExternalContainer,
  ExternalHeaderSpacing,
} from '@/app/common/styled.ts';
import CSW from '@/app/common/CSW/CSW';
import type { CSWProps } from '@/app/common/CSW/services/types.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

export type HeaderManagerProps = {
  externalHeaderEnabled: boolean;
  externalHeaderHTML: string | null;
  /** Vetoes the CSW in every branch below — see showsCsw for the rule and why it is enforced here. */
  showCsw: boolean;
  appShowCsw: boolean;
  brand: Brand;
  contentHubHeaderProps?: ContentHubHeaderProps;
  cswProps: CSWProps;
  headerFooterStaticEnabled?: boolean;
};

const HeaderManager = ({
  externalHeaderEnabled,
  externalHeaderHTML,
  showCsw,
  appShowCsw,
  brand,
  contentHubHeaderProps,
  cswProps,
  headerFooterStaticEnabled,
}: HeaderManagerProps) => {
  const showHolidayPiratesHeader = brand === Brand.HOLIDAYPIRATES;
  const showHolidayGuruHeader = brand === Brand.HOLIDAYGURU;
  const { commonData } = usePageWrapperData();
  const { isApp } = commonData;

  if (externalHeaderEnabled) {
    return (
      <ExternalHeaderSpacing brand={brand} clearFixedHeader={!showHolidayGuruHeader} isApp={isApp}>
        {showHolidayPiratesHeader && <ExternalHeader externalHeaderHTML={externalHeaderHTML} />}
        {showHolidayGuruHeader && <HtmlInjector htmlString={externalHeaderHTML} />}
        {showCsw && (
          <CSWExternalContainer brand={brand}>
            <InnerSlot setIsSearchCollapsed={() => {}}>
              <CSW {...cswProps} />
            </InnerSlot>
          </CSWExternalContainer>
        )}
      </ExternalHeaderSpacing>
    );
  }

  // MPH-395 workaround (see CSWAppContainer in styled.ts): render the CSW outside
  // the ContentHub nav so the app's injected `display: none` can't reach it.
  if (isApp && appShowCsw) {
    // Null, never a fall-through to the web ContentHub header below: in the app this header is the
    // CSW and nothing else. (MPH-1159)
    return showCsw ? (
      <>
        <CSWAppExpandedOffsetReset />
        <CSWAppContainer>
          <InnerSlot setIsSearchCollapsed={() => {}}>
            <CSW {...cswProps} />
          </InnerSlot>
        </CSWAppContainer>
      </>
    ) : null;
  }

  if (contentHubHeaderProps && isHeaderPropsValid(contentHubHeaderProps)) {
    // Keyed so the header remounts when the slot appears or disappears. The ContentHub nav seeds
    // its collapsed/sticky search state from `useState(!!innerSlot)`, which React ignores on a
    // re-render, and its correcting effect bails on the portal ref it no longer has — so without a
    // remount an in-funnel navigation onto the review step leaves that state stale. (MPH-1159)
    return showCsw ? (
      <ContentHubHeader
        key="with-csw"
        {...contentHubHeaderProps}
        innerSlot={<CSW {...cswProps} />}
        InnerSlotComponent={InnerSlot}
      />
    ) : (
      // Left in its no-search state: the nav's collapsed-search mode exists to make room for the
      // widget, and forcing it on without one leaves the small logo stranded in an empty bar.
      <ContentHubHeader key="no-csw" {...contentHubHeaderProps} />
    );
  }

  if (headerFooterStaticEnabled) return <StaticHeader />;

  return null;
};

export default HeaderManager;
